/**
 * frontend/src/pages/valuation/_OverviewParticulars.tsx
 *
 * VAL-OVERVIEW-1C R5 — the ten VS Section 100 particulars, read-and-jump.
 * Values already live on the engagement fetched by the parent — no second
 * fetch. Intake owns these fields; this block has no editor of its own
 * (VAL-INTAKE-TUNING-SPLIT-1 is the defect two editors on one row repeats).
 */
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import type { ValuationEngagement } from '@/types/valuation'
import { STANDARDS_OF_VALUE } from './_IntakeParticulars'

const PARTICULARS_FIELDS: Array<{ field: keyof ValuationEngagement; label: string }> = [
  { field: 'prepared_for', label: 'Prepared for' },
  { field: 'purpose', label: 'Purpose of the engagement' },
  { field: 'intended_users', label: 'Intended users' },
  { field: 'standard_of_value', label: 'Standard of value' },
  { field: 'premise_of_value', label: 'Premise of value' },
  { field: 'subject_interest', label: 'Subject interest' },
  { field: 'interest_characteristics', label: 'Interest characteristics' },
  { field: 'sources_relied_upon', label: 'Sources relied upon' },
  { field: 'analyst_has_financial_interest', label: 'Analyst has a financial interest' },
  { field: 'fee_is_contingent', label: 'Fee is contingent' },
]

const BOOLEAN_FIELDS = new Set<keyof ValuationEngagement>([
  'analyst_has_financial_interest',
  'fee_is_contingent',
])

function displayValue(field: keyof ValuationEngagement, value: unknown): { text: string; unset: boolean } {
  if (BOOLEAN_FIELDS.has(field)) {
    if (typeof value !== 'boolean') return { text: 'Not set', unset: true }
    return { text: value ? 'Yes' : 'No', unset: false }
  }
  if (value === null || value === undefined || value === '') return { text: 'Not set', unset: true }
  if (field === 'standard_of_value') {
    return { text: STANDARDS_OF_VALUE.find(o => o.value === value)?.label ?? String(value), unset: false }
  }
  return { text: String(value), unset: false }
}

export function OverviewParticulars({ id, engagement }: { id: string; engagement: ValuationEngagement }) {
  const navigate = useNavigate()

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>Particulars</CardTitle>
        <Button size="sm" variant="outline" onClick={() => navigate(`/valuation/${id}/intake#particulars`)}>
          Edit particulars
        </Button>
      </CardHeader>
      <CardContent>
        <dl className="space-y-3">
          {PARTICULARS_FIELDS.map(({ field, label }) => {
            const { text, unset } = displayValue(field, engagement[field])
            return (
              <div key={field} className="flex flex-col gap-1 sm:flex-row sm:justify-between sm:gap-3">
                <dt className="text-sm text-gray-500">{label}</dt>
                <dd className={`text-sm sm:text-right ${unset ? 'text-destructive' : 'text-gray-900'}`}>
                  {text}
                </dd>
              </div>
            )
          })}
        </dl>
      </CardContent>
    </Card>
  )
}
